import {Component, inject, Input} from '@angular/core';
import {map, Observable, shareReplay} from 'rxjs';
import {
    EmptyTrailerScoreQuery,
    EmptyTrailerScoreType,
    IMyScoreShiftQuery,
    PermissionType,
    ScoreDto,
    ShuntProductivityScoreQuery,
    YardCapacityScoreQuery,
    YardCapacityScoreType,
    YardManagementClient,
} from '@services';
import {DoughnutChartComponent} from '../../../components/library/doughnut-chart/doughnut-chart.component';
import {CapitalCasePipe} from '../../../pipes/capital-case.pipe';
import {
    yardCapacityBreakdownPalette,
    yardCapacityChartScore,
    yardCapacityPalette,
} from '../../yard-management/yard-score/yard-capacity-score/yard-capacity-score.component';
import {shuntProductivityPalette} from '../../yard-management/yard-score/shunt-productivity-score/shunt-productivity-score.component';
import {emptyTrailerPalette} from '../../yard-management/yard-score/empty-trailer-score/empty-trailer-score.component';
import {DashboardAccess} from '../dashboard-access';
import {DashboardCardComponent} from '../dashboard-card/dashboard-card.component';

type YardCapacityScores = {[key in keyof typeof YardCapacityScoreType]?: ScoreDto};

@Component({
    selector: 'app-dashboard-our-yard',
    imports: [DoughnutChartComponent, DashboardCardComponent, CapitalCasePipe],
    templateUrl: './dashboard-our-yard.component.html',
})
export class DashboardOurYardComponent {
    @Input() from: Date;
    @Input() to: Date;

    protected access = inject(DashboardAccess);
    private yardManagementClient = inject(YardManagementClient);

    protected readonly palettes = {
        yardCapacity: yardCapacityPalette(),
        yardCapacityBreakdown: yardCapacityBreakdownPalette(),
        shuntProductivity: shuntProductivityPalette(),
        emptyTrailerAging: emptyTrailerPalette(),
    };

    protected readonly shuntUserId = this.access.ownUnlessManage(PermissionType.ShuntProductivityScore);

    protected readonly yardCapacityBreakdowns = [
        YardCapacityScoreType.Controllable,
        YardCapacityScoreType.Uncontrollable,
    ].map(scoreType => ({
        scoreType,
        loadData: (query: IMyScoreShiftQuery) =>
            this.yardCapacityScores(query).pipe(map(scores => scores[scoreType] ?? new ScoreDto())),
    }));

    private yardCapacityRequest: {range: string; scores: Observable<YardCapacityScores>} | null = null;

    protected loadYardCapacity = (query: IMyScoreShiftQuery) =>
        this.yardCapacityScores(query).pipe(map(scores => yardCapacityChartScore(scores[YardCapacityScoreType.MainScore])));

    protected loadShuntProductivity = (query: IMyScoreShiftQuery) =>
        this.yardManagementClient.getShuntProductivityScore(new ShuntProductivityScoreQuery({
            from: query.from,
            to: query.to,
            userId: query.userId ?? undefined,
        }));

    protected loadEmptyTrailerAging = (query: IMyScoreShiftQuery) =>
        this.yardManagementClient
            .getEmptyTrailerScore(new EmptyTrailerScoreQuery({from: query.from, to: query.to}))
            .pipe(map(scores => scores[EmptyTrailerScoreType.MainScore] ?? new ScoreDto()));

    // Yard Capacity, Controllable and Uncontrollable all come from one response, so their cards share one request per range.
    private yardCapacityScores(query: IMyScoreShiftQuery): Observable<YardCapacityScores> {
        const range = JSON.stringify([query.from, query.to]);
        if (this.yardCapacityRequest?.range !== range) {
            this.yardCapacityRequest = {
                range,
                scores: this.yardManagementClient
                    .getYardCapacityScore(new YardCapacityScoreQuery({from: query.from, to: query.to}))
                    .pipe(shareReplay({bufferSize: 1, refCount: true})),
            };
        }
        return this.yardCapacityRequest.scores;
    }
}
